// Alias scope resolver — walks FROM/JOIN/USING/CROSS APPLY/OUTER APPLY/
// LATERAL inside one statement and returns the `AliasBinding[]` visible at
// the cursor. Implements UNION branch isolation, correlated-subquery outer
// visibility, LATERAL left-side-on-right-side, and self-join independence.
// Source-driven (token stream); pseudo-tables appended via `pseudoTables.ts`.

import type {
  AliasBinding,
  CteBinding,
  DatabaseEngineId,
  OffsetRange,
} from '../../../core/engines';
import { SUBQUERY_OPENING_KEYWORDS } from '../context/keywords';
import { tokenize } from '../context/tokenizer';

import { parseIdentifierToken } from './identifierUtils';
import { collectPseudoTables } from './pseudoTables';
import { findMatchingRparen, isSelectInside, parseRelationName } from './relationParser';

const RELATION_INTRODUCERS: ReadonlySet<string> = new Set([
  // F5.2-CB2 — `MERGE` introduces the target relation when used as
  // `MERGE <table> AS alias …` (MSSQL/Oracle/PG14+ syntax). The
  // `MERGE INTO <table>` form is also handled (INTO already introduces).
  'FROM', 'JOIN', 'APPLY', 'LATERAL', 'USING', 'UPDATE', 'INTO', 'MERGE',
]);
const CLAUSE_BOUNDARIES: ReadonlySet<string> = new Set([
  'WHERE', 'GROUP', 'HAVING', 'ORDER', 'LIMIT', 'OFFSET', 'FETCH',
  'SELECT', 'RETURNING', 'SET', 'VALUES', 'ON',
]);
// `UNPIVOT [INCLUDE NULLS | EXCLUDE NULLS] (…)` — modifier words that may sit
// between the (UN)PIVOT keyword and its opening paren.
const PIVOT_NULL_MODIFIERS: ReadonlySet<string> = new Set(['INCLUDE', 'EXCLUDE', 'NULLS']);
// Alias for an unaliased pivoted inline view, so its (pivoted) columns still
// resolve in the outer query. Underscore-prefixed to avoid colliding with a
// user-written alias. Exported so resolvers can suppress it where the alias
// NAME would be offered as a candidate (it is internal — the user cannot type
// it), while still expanding its columns.
export const PIVOT_SYNTHETIC_ALIAS = '__pivot';

type Scope = {
  parentBranchIdx: number;
  isRelationOpener: boolean;
  /** Position of the opening `(` for relation-opener parens. Lets the
   *  rparen handler emit `bodyRange` on the subquery alias binding so
   *  resolvers can infer the projected columns. */
  lparenStart?: number;
};

type Branch = {
  scopeIdx: number;
  bindings: AliasBinding[];
};

type PendingSubqueryAlias = {
  outerBranchIdx: number;
  rangeStart: number;
  rangeEnd: number;
  /** Body span `(...)` of the subquery — start at the opening paren, end
   *  at the closing paren inclusive. Forwarded to the alias binding so
   *  resolvers can infer projected columns. */
  bodyStart: number;
  bodyEnd: number;
};

export function resolveAliasBindings(
  source: string,
  statementRange: OffsetRange,
  cursor: number,
  engineId: DatabaseEngineId,
  cteBindings: readonly CteBinding[] = [],
): readonly AliasBinding[] {
  const tokens = tokenize(source, statementRange.start, statementRange.end, engineId);
  const cteNamesLower = new Set(cteBindings.map((c) => c.name.toLowerCase()));

  const scopes: Scope[] = [{ parentBranchIdx: -1, isRelationOpener: false }];
  const branches: Branch[] = [{ scopeIdx: 0, bindings: [] }];
  let currentScopeIdx = 0;
  let currentBranchIdx = 0;
  let cursorBranchIdx = -1;
  let pendingRelation = false;
  let pendingIntroducer: string | null = null;
  let pendingSubqueryAlias: PendingSubqueryAlias | null = null;
  let prevKw: string = '';
  // Per-lparen flag — true if that lparen pushed a fresh subquery/relation
  // scope. Tracking this lets the matching rparen know whether to pop the
  // scope stack. Expression parens (`COUNT(...)`, `OVER (...)`, CASE arms,
  // value lists, …) are transparent — they don't break alias visibility.
  const lparenPushedScope: boolean[] = [];

  for (let i = 0; i < tokens.length; i += 1) {
    const tok = tokens[i];
    if (cursorBranchIdx === -1 && tok.start >= cursor) cursorBranchIdx = currentBranchIdx;

    if (pendingSubqueryAlias) {
      if (tok.type === 'kw' && tok.text.toUpperCase() === 'AS') continue;
      // `( … ) PIVOT ( … ) [alias]` / `UNPIVOT [INCLUDE|EXCLUDE NULLS] ( … )` —
      // the PIVOT keyword is NOT the alias. Skip the clause, capture its span,
      // and bind the inline view immediately: under the explicit trailing
      // alias if present (Oracle `) p`), otherwise under a synthetic one so
      // the pivoted columns still resolve in the outer query (Oracle allows an
      // unaliased pivoted inline view). PIVOT/UNPIVOT may tokenize as a keyword
      // OR a bare identifier depending on the dialect.
      const word =
        tok.type === 'kw' || tok.type === 'ident' ? tok.text.toUpperCase() : '';
      if (word === 'PIVOT' || word === 'UNPIVOT') {
        let openIdx = i + 1;
        while (
          openIdx < tokens.length &&
          (tokens[openIdx].type === 'ident' || tokens[openIdx].type === 'kw') &&
          PIVOT_NULL_MODIFIERS.has(tokens[openIdx].text.toUpperCase())
        ) {
          openIdx += 1;
        }
        const openTok = tokens[openIdx];
        if (openTok?.type === 'lparen') {
          const closeIdx = findMatchingRparen(tokens, openIdx);
          // `closeIdx === -1` ⇒ the clause is still being typed (no closing
          // paren yet): extend the pivot span past the buffer end so the
          // interior still resolves, with no trailing alias to read.
          // The output alias may be preceded by an optional `AS` — T-SQL
          // writes `… ) AS pvt`, Oracle `… ) p`.
          let aliasIdx = closeIdx !== -1 ? closeIdx + 1 : -1;
          if (
            aliasIdx !== -1 &&
            tokens[aliasIdx]?.type === 'kw' &&
            tokens[aliasIdx].text.toUpperCase() === 'AS'
          ) {
            aliasIdx += 1;
          }
          const aliasTok = aliasIdx !== -1 ? tokens[aliasIdx] : undefined;
          const hasAlias = aliasTok?.type === 'ident';
          const alias = hasAlias
            ? parseIdentifierToken(aliasTok!.text).content
            : PIVOT_SYNTHETIC_ALIAS;
          const pivotEnd =
            closeIdx !== -1 ? tokens[closeIdx].end : statementRange.end + 1;
          const definedEnd = hasAlias ? aliasTok!.end : pivotEnd;
          branches[pendingSubqueryAlias.outerBranchIdx].bindings.push({
            alias,
            relation: '__subquery',
            kind: 'subquery',
            // Provenance (T6a): the synthetic alias has no source spelling —
            // leave both fields undefined so it is never emitted verbatim.
            ...(hasAlias ? { explicitAlias: true, aliasSql: aliasTok!.text } : {}),
            definedAt: { start: pendingSubqueryAlias.rangeStart, end: definedEnd },
            bodyRange: {
              start: pendingSubqueryAlias.bodyStart,
              end: pendingSubqueryAlias.bodyEnd,
            },
            pivotRange: { start: openTok.start, end: pivotEnd },
          });
          pendingSubqueryAlias = null;
          i = hasAlias ? aliasIdx : closeIdx !== -1 ? closeIdx : tokens.length - 1;
          continue;
        }
      }
      if (tok.type === 'ident' || tok.type === 'kw') {
        // T-SQL `( … ) src PIVOT (…) AS pvt` — a SOURCE alias sits between the
        // closing paren and the PIVOT keyword. The PIVOT consumes the source,
        // so `src` is NOT visible in the outer query (only the transformed
        // `pvt` is). Skip it WITHOUT binding and keep the pending alias alive
        // so the PIVOT branch binds the OUTPUT; the pivot interior recovers the
        // source name by scanning the gap text. (Oracle has no source alias —
        // its PIVOT keyword follows the closing paren directly.)
        const nextTok = tokens[i + 1];
        const nextWord =
          nextTok && (nextTok.type === 'kw' || nextTok.type === 'ident')
            ? nextTok.text.toUpperCase()
            : '';
        if (nextWord === 'PIVOT' || nextWord === 'UNPIVOT') {
          continue;
        }
        const ident = parseIdentifierToken(tok.text);
        branches[pendingSubqueryAlias.outerBranchIdx].bindings.push({
          alias: ident.content,
          relation: '__subquery',
          kind: 'subquery',
          explicitAlias: true,
          aliasSql: tok.text,
          definedAt: { start: pendingSubqueryAlias.rangeStart, end: tok.end },
          bodyRange: {
            start: pendingSubqueryAlias.bodyStart,
            end: pendingSubqueryAlias.bodyEnd,
          },
        });
        pendingSubqueryAlias = null;
        continue;
      }
      pendingSubqueryAlias = null;
      // fall through to process tok normally.
    }

    if (tok.type === 'lparen') {
      if (pendingRelation && pendingIntroducer === 'USING' && !isSelectInside(tokens, i)) {
        const matching = findMatchingRparen(tokens, i);
        if (matching === -1) { i = tokens.length; break; }
        i = matching;
        pendingRelation = false;
        pendingIntroducer = null;
        prevKw = '';
        continue;
      }
      const isRelOpener = pendingRelation;
      // Only push a fresh alias scope when the paren actually opens a
      // subquery (or relation slot). Expression parens (`OVER (PARTITION
      // BY …)`, `COUNT(*)`, CASE arms, `IN (1,2,3)`) are transparent so
      // outer-statement aliases (e.g. `FROM orders o` defined later in
      // source) stay visible inside them — mirrors real SQL scoping where
      // the FROM clause is evaluated before SELECT.
      const opensSubquery =
        isRelOpener || SUBQUERY_OPENING_KEYWORDS.has(prevKw) || isSelectInside(tokens, i);
      if (opensSubquery) {
        scopes.push({
          parentBranchIdx: currentBranchIdx,
          isRelationOpener: isRelOpener,
          lparenStart: tok.start,
        });
        const newScopeIdx = scopes.length - 1;
        branches.push({ scopeIdx: newScopeIdx, bindings: [] });
        const newBranchIdx = branches.length - 1;
        currentScopeIdx = newScopeIdx;
        currentBranchIdx = newBranchIdx;
        lparenPushedScope.push(true);
      } else {
        lparenPushedScope.push(false);
      }
      pendingRelation = false;
      pendingIntroducer = null;
      prevKw = '';
      continue;
    }

    if (tok.type === 'rparen') {
      const pushed = lparenPushedScope.pop();
      if (pushed !== true) {
        // Expression paren — nothing to pop. Reset any pending state and
        // continue with the outer frame unchanged.
        prevKw = '';
        continue;
      }
      const closingScope = scopes[currentScopeIdx];
      if (closingScope.parentBranchIdx === -1) {
        prevKw = '';
        continue;
      }
      const outerBranchIdx = closingScope.parentBranchIdx;
      const wasRelOpener = closingScope.isRelationOpener;
      const lparenStart = closingScope.lparenStart ?? tok.start;
      currentScopeIdx = branches[outerBranchIdx].scopeIdx;
      currentBranchIdx = outerBranchIdx;
      if (wasRelOpener) {
        pendingSubqueryAlias = {
          outerBranchIdx,
          rangeStart: tok.start,
          rangeEnd: tok.end,
          bodyStart: lparenStart,
          bodyEnd: tok.end,
        };
      }
      prevKw = '';
      continue;
    }

    if (tok.type === 'kw') {
      const kw = tok.text.toUpperCase();
      prevKw = kw;
      if (kw === 'UNION' || kw === 'INTERSECT' || kw === 'EXCEPT') {
        branches.push({ scopeIdx: currentScopeIdx, bindings: [] });
        currentBranchIdx = branches.length - 1;
        pendingRelation = false;
        pendingIntroducer = null;
        continue;
      }
      if (RELATION_INTRODUCERS.has(kw)) {
        pendingRelation = true;
        pendingIntroducer = kw;
        continue;
      }
      if (CLAUSE_BOUNDARIES.has(kw)) {
        pendingRelation = false;
        pendingIntroducer = null;
        continue;
      }
      continue;
    }

    if (tok.type === 'comma') {
      prevKw = '';
      if (pendingIntroducer && (pendingIntroducer === 'FROM' || pendingIntroducer === 'JOIN' || pendingIntroducer === 'APPLY' || pendingIntroducer === 'LATERAL')) {
        pendingRelation = true;
      }
      continue;
    }

    if (pendingRelation && tok.type === 'ident') {
      const result = parseRelationName(tokens, i, pendingIntroducer ?? undefined);
      if (!result) { pendingRelation = false; pendingIntroducer = null; continue; }
      const lower = result.relation.toLowerCase();
      const kind: AliasBinding['kind'] = result.kind === 'derived'
        ? 'derived'
        : cteNamesLower.has(lower) ? 'cte' : 'table';
      branches[currentBranchIdx].bindings.push({
        alias: result.alias,
        relation: result.relation,
        ...(result.schema !== undefined ? { schema: result.schema } : {}),
        ...(result.database !== undefined ? { database: result.database } : {}),
        kind,
        explicitAlias: result.explicitAlias,
        aliasSql: result.aliasSql,
        definedAt: { start: tokens[i].start, end: tokens[result.lastConsumedIdx].end },
        ...(result.syntheticColumns ? { syntheticColumns: result.syntheticColumns } : {}),
      });
      i = result.lastConsumedIdx;
      pendingRelation = false;
      continue;
    }

    if (tok.type === 'ident' || tok.type === 'literal' || tok.type === 'op' || tok.type === 'dot') {
      pendingRelation = false;
      prevKw = '';
      continue;
    }
  }

  if (cursorBranchIdx === -1) cursorBranchIdx = currentBranchIdx;

  // Visibility rule per branch:
  //   * Cursor's own branch — every binding in this branch is visible
  //     regardless of where it appears in source-text. SQL aliases are
  //     visible to the whole statement they're in (so `SELECT … FROM t`
  //     with the cursor in the SELECT-list still sees `t`).
  //   * Parent branches — only bindings whose `definedAt.start < cursor`.
  //     This handles the `FROM (SELECT * FROM t WHERE |) AS s` case where
  //     the outer-scope alias `s` is defined AFTER the cursor (its
  //     definedAt starts at the closing `)`). The cursor is conceptually
  //     "inside" the alias's defining range, so `s` is not yet bound.
  const visible: AliasBinding[] = [];
  let bIdx = cursorBranchIdx;
  let isCursorBranch = true;
  while (bIdx !== -1) {
    for (const b of branches[bIdx].bindings) {
      if (isCursorBranch || b.definedAt.start < cursor) visible.push(b);
    }
    const scope = scopes[branches[bIdx].scopeIdx];
    bIdx = scope.parentBranchIdx;
    isCursorBranch = false;
  }

  visible.push(...collectPseudoTables(source, statementRange, engineId));
  return visible;
}

